import React from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { Car, MessageCircle, Shield, CreditCard, Star, MapPin, Users, Bell, Smartphone, UserCheck, Banknote, Globe, MessageSquare } from "lucide-react";
import Footer from "../footer/Footer";
import './landing.css';

const features = [
  { icon: Car, title: "Offer & Request Rides", desc: "Post a ride you're offering or request one you need. Set your route, date, fare, and available seats." },
  { icon: MessageCircle, title: "Real-Time Chat", desc: "Chat with your driver and co-passengers in real-time. Coordinate pickup points and stay connected throughout your trip." },
  { icon: CreditCard, title: "Flexible Payments", desc: "Pay with JazzCash, EasyPaisa, or Stripe. Choose the method that works best for you — no credit card required." },
  { icon: Bell, title: "Instant Notifications", desc: "Get notified the moment someone books your ride, sends a message, or when your ride status changes." },
  { icon: Star, title: "Reviews & Ratings", desc: "Build your reputation through post-ride reviews. See ratings before you book so you always ride with trusted people." },
  { icon: Users, title: "Community Forum", desc: "Join discussions about routes, travel tips, and carpooling experiences. Connect with fellow travelers beyond just rides." },
  { icon: MapPin, title: "Interactive Maps", desc: "Pick your exact pickup and dropoff locations on an interactive map. No more confusion about meeting points." },
  { icon: Shield, title: "Safe & Moderated", desc: "Email verification, user reporting, and active admin moderation keep the platform safe for everyone." },
];

const steps = [
  { num: 1, title: "Create Your Account", desc: "Sign up with email or Google in seconds. Verify your identity to build trust." },
  { num: 2, title: "Find or Post a Ride", desc: "Search for rides on your route or offer empty seats in your car. Set your price and preferences." },
  { num: 3, title: "Book & Chat", desc: "Reserve your seat and coordinate details with your driver through in-app chat." },
  { num: 4, title: "Ride, Pay & Review", desc: "Travel together, pay through the app, and leave a review to help the community." },
];

const advantages = [
  { icon: Banknote, title: "Built for Pakistan's Budget", desc: "\"Sasti\" means affordable. We're designed for cost-conscious travelers, not premium ride-hailing. Save up to 60% compared to bus fares." },
  { icon: Smartphone, title: "Local Payment Methods", desc: "JazzCash and EasyPaisa built-in — not just international credit cards. Pay the way Pakistan pays." },
  { icon: Users, title: "Community, Not Just Transactions", desc: "Our discussion forum and community features make SastiRide more than a booking app. Build relationships with regular co-travelers." },
  { icon: Shield, title: "Verified & Transparent", desc: "Email verification, public reviews, user reporting, and admin moderation. No anonymous riders, no hidden fees." },
  { icon: Globe, title: "90+ Cities Connected", desc: "From Lahore to Islamabad, Karachi to Peshawar — intercity and local routes across all of Pakistan." },
  { icon: MessageCircle, title: "Real-Time Everything", desc: "Live chat, instant notifications, and real-time ride status updates. Always know what's happening with your ride." },
];

const routes = [
  { from: "Lahore", to: "Islamabad", fare: "1,500" },
  { from: "Karachi", to: "Hyderabad", fare: "800" },
  { from: "Islamabad", to: "Peshawar", fare: "1,000" },
  { from: "Lahore", to: "Faisalabad", fare: "600" },
  { from: "Karachi", to: "Lahore", fare: "3,500" },
  { from: "Islamabad", to: "Murree", fare: "500" },
];

const trustItems = [
  { icon: UserCheck, title: "Verified Profiles", desc: "Every user verifies their email. See complete profiles with ratings before you ride." },
  { icon: Shield, title: "Report & Moderate", desc: "Flag suspicious users or behavior. Our admin team reviews reports and takes action." },
  { icon: Star, title: "Honest Reviews", desc: "Post-ride ratings you can trust. No fake reviews — only verified passengers and drivers can rate." },
  { icon: MessageSquare, title: "In-App Communication", desc: "Keep all communication on-platform. No need to share personal phone numbers." },
];

const Landing = ({ isLoggedIn }) => {
  const ctaLink = isLoggedIn ? "/" : "/login";

  return (
    <div className="sr-landing">
      <Helmet>
        <title>SastiRide - Affordable Ride Sharing & Carpooling in Pakistan</title>
        <meta name="description" content="Share rides, split costs, and travel safely between 90+ cities in Pakistan. SastiRide connects drivers and passengers for easy, affordable carpooling with JazzCash, EasyPaisa & Stripe payments." />
        <link rel="canonical" href="https://sastiride.com/about" />
        <meta property="og:title" content="SastiRide - Pakistan's #1 Carpooling Platform" />
        <meta property="og:description" content="Save up to 60% on intercity travel. Share rides with verified drivers and passengers across Pakistan." />
        <meta property="og:url" content="https://sastiride.com/about" />
      </Helmet>

      {/* Hero */}
      <section className="sr-landing__hero">
        <span className="sr-landing__hero-badge">Pakistan's Carpooling Community</span>
        <h1 className="sr-landing__hero-title">Travel Anywhere in Pakistan — For Less</h1>
        <p className="sr-landing__hero-subtitle">
          Share rides with verified drivers and passengers. Save up to 60% on intercity travel with SastiRide — Pakistan's community-driven carpooling platform.
        </p>
        <div className="sr-landing__hero-ctas">
          <Link to="/login" className="sr-landing__btn--primary">Get Started — It's Free</Link>
          <Link to={ctaLink} className="sr-landing__btn--secondary">Browse Rides</Link>
        </div>
        <div className="sr-landing__hero-stats">
          <div className="sr-landing__stat">
            <div className="sr-landing__stat-number">500+</div>
            <div className="sr-landing__stat-label">Rides Shared</div>
          </div>
          <div className="sr-landing__stat">
            <div className="sr-landing__stat-number">1,000+</div>
            <div className="sr-landing__stat-label">Community Members</div>
          </div>
          <div className="sr-landing__stat">
            <div className="sr-landing__stat-number">90+</div>
            <div className="sr-landing__stat-label">Cities Connected</div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="sr-landing__section sr-landing__section--light">
        <div className="sr-landing__section-content">
          <p className="sr-landing__section-label">Features</p>
          <h2 className="sr-landing__section-title">Everything You Need for Shared Travel</h2>
          <div className="sr-landing__features-grid">
            {features.map((f, i) => (
              <div key={i} className="sr-landing__feature-card">
                <div className="sr-landing__feature-icon">
                  <f.icon size={22} />
                </div>
                <h3 className="sr-landing__feature-title">{f.title}</h3>
                <p className="sr-landing__feature-desc">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="sr-landing__section sr-landing__section--dark">
        <div className="sr-landing__section-content">
          <p className="sr-landing__section-label">How It Works</p>
          <h2 className="sr-landing__section-title">Get Started in 4 Simple Steps</h2>
          <div className="sr-landing__steps">
            {steps.map((s) => (
              <div key={s.num} className="sr-landing__step">
                <div className="sr-landing__step-number">{s.num}</div>
                <h3 className="sr-landing__step-title">{s.title}</h3>
                <p className="sr-landing__step-desc">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why SastiRide */}
      <section className="sr-landing__section sr-landing__section--dark">
        <div className="sr-landing__section-content">
          <p className="sr-landing__section-label">Why SastiRide</p>
          <h2 className="sr-landing__section-title">Built Different — Built for Pakistan</h2>
          <div className="sr-landing__advantages-grid">
            {advantages.map((a, i) => (
              <div key={i} className="sr-landing__advantage-card">
                <div className="sr-landing__advantage-icon">
                  <a.icon size={28} />
                </div>
                <h3 className="sr-landing__advantage-title">{a.title}</h3>
                <p className="sr-landing__advantage-desc">{a.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Popular Routes */}
      <section className="sr-landing__section sr-landing__section--dark">
        <div className="sr-landing__section-content">
          <p className="sr-landing__section-label">Popular Routes</p>
          <h2 className="sr-landing__section-title">Where Pakistanis Are Sharing Rides</h2>
          <div className="sr-landing__routes-grid">
            {routes.map((r, i) => (
              <Link key={i} to={ctaLink} className="sr-landing__route-card">
                <div className="sr-landing__route-cities">{r.from} → {r.to}</div>
                <div className="sr-landing__route-meta">Starting from Rs. {r.fare}</div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Trust & Safety */}
      <section className="sr-landing__section sr-landing__section--light">
        <div className="sr-landing__section-content">
          <p className="sr-landing__section-label">Trust & Safety</p>
          <h2 className="sr-landing__section-title">Your Safety Is Our Priority</h2>
          <div className="sr-landing__trust-grid">
            {trustItems.map((t, i) => (
              <div key={i} className="sr-landing__trust-item">
                <div className="sr-landing__trust-icon">
                  <t.icon size={20} />
                </div>
                <div className="sr-landing__trust-text">
                  <h3>{t.title}</h3>
                  <p>{t.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="sr-landing__cta-final">
        <div className="sr-landing__cta-bar" />
        <h2 className="sr-landing__cta-title">Ready to Save on Your Next Trip?</h2>
        <p className="sr-landing__cta-subtitle">
          Join thousands of Pakistanis who are already sharing rides and splitting costs.
        </p>
        <div className="sr-landing__cta-buttons">
          <Link to="/login" className="sr-landing__btn--primary">Create Free Account</Link>
        </div>
        <p className="sr-landing__cta-signin">
          Already have an account? <Link to="/login">Sign in</Link>
        </p>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Landing;
